import { Presence } from "@chakra-ui/react";
import type { SlideSharedProps } from "./Slide.props";

export default function Slide({ children, ...props }: SlideProps): JSX.Element {
  // Extract props explicitly so we can control prop names in one place
  const {
    in: isOpen,
    open,
    unmountOnCollapse,
    unmountOnExit,
    direction,
    transition,
    animateOpacity,
    startingHeight: _startingHeight,
    endingHeight: _endingHeight,
    ...rest
  } = props;

  // Map v1/v2 props to v3 props for backward compatibility
  const openValue = open ?? isOpen ?? false;
  const unmountValue = unmountOnExit ?? unmountOnCollapse;

  const from = (() => {
    switch (direction) {
      case "top":
        return { open: "slide-from-top-full", closed: "slide-to-top-full" };
      case "left":
        return { open: "slide-from-left-full", closed: "slide-to-left-full" };
      case "right":
        return { open: "slide-from-right-full", closed: "slide-to-right-full" };
      case "bottom":
      default:
        return {
          open: "slide-from-bottom-full",
          closed: "slide-to-bottom-full",
        };
    }
  })();

  const enterDuration = transition?.enter?.duration;
  const exitDuration = transition?.exit?.duration;
  const animationDuration =
    enterDuration ?? exitDuration ?? rest.animationDuration ?? "moderate";

  const animationName = animateOpacity
    ? { _open: `${from.open}, fade-in`, _closed: `${from.closed}, fade-out` }
    : { _open: from.open, _closed: from.closed };

  return (
    <Presence
      present={openValue}
      unmountOnExit={unmountValue}
      aria-hidden={openValue ? undefined : "true"}
      animationName={animationName}
      animationDuration={animationDuration}
      {...rest}
    >
      {children}
    </Presence>
  );
}

export type SlideProps = SlideSharedProps & {
  // Add any web specific props here.
};
